/** Tag chips, used on note rows, the note view and the search filters. */

import { el } from "../lib/dom";
import { searchPath } from "../router/paths";

export interface TagChipOptions {
  readonly tag: string;
  readonly count?: number;
  /** When set the chip becomes a button instead of a link to a tag search. */
  readonly onSelect?: (tag: string) => void;
  readonly selected?: boolean;
  /**
   * Take the chip out of the Tab order (still clickable). Set on list rows,
   * where Tab should step from one note title to the next rather than through
   * every tag in between; a tag search is a click or a trip to the search page.
   */
  readonly skipTab?: boolean;
}

export function tagChip(options: TagChipOptions): HTMLElement {
  const label = options.count === undefined ? options.tag : `${options.tag}`;
  const className = `tag-chip${options.selected === true ? " tag-chip--selected" : ""}`;
  const tabAttrs = options.skipTab === true ? { tabindex: -1 } : {};

  const children = [
    el("span", { className: "tag-chip__label", text: label }),
    options.count === undefined ? null : el("span", { className: "tag-chip__count", text: String(options.count) }),
  ];

  if (options.onSelect !== undefined) {
    const onSelect = options.onSelect;
    return el("button", {
      className,
      attrs: { type: "button", "aria-pressed": options.selected === true, ...tabAttrs },
      children,
      on: {
        click: (): void => {
          onSelect(options.tag);
        },
      },
    });
  }

  return el("a", {
    className,
    attrs: { href: searchPath({ tags: [options.tag] }), ...tabAttrs },
    children,
  });
}

export interface TagListOptions {
  /** See `TagChipOptions.skipTab`. */
  readonly skipTab?: boolean;
}

export function tagList(tags: readonly string[], options: TagListOptions = {}): HTMLElement | null {
  if (tags.length === 0) {
    return null;
  }
  const chipOptions = options.skipTab === true ? { skipTab: true } : {};
  return el("ul", {
    className: "tag-list",
    children: tags.map((tag) => el("li", { children: [tagChip({ tag, ...chipOptions })] })),
  });
}
